import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { AuthContext } from '../App';

export default function SettingsPage() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  // Profile
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  // Change password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwStatus, setPwStatus] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  // 2FA
  const [twofa, setTwofa] = useState({ enabled: false, method: null, contact: null });
  const [tfaMethod, setTfaMethod] = useState('email');
  const [tfaContact, setTfaContact] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [tfaStep, setTfaStep] = useState('idle'); // idle, pending, verify
  const [tfaStatus, setTfaStatus] = useState('');
  const [tfaLoading, setTfaLoading] = useState(false);

  useEffect(() => {
    if (user) api.get2fa().then(setTwofa).catch(() => {});
  }, [user]);

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleSave = async () => {
    setSaving(true);
    setStatus('');
    try {
      const res = await api.updateProfile({ display_name: displayName, bio, avatar_url: avatarUrl });
      setUser(res.user);
      setStatus('Profile updated!');
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    } finally { setSaving(false); }
    setTimeout(() => setStatus(''), 4000);
  };

  const handleChangePassword = async () => {
    setPwStatus('');
    if (newPw !== confirmPw) { setPwStatus('Error: Passwords do not match'); return; }
    if (newPw.length < 8) { setPwStatus('Error: New password must be at least 8 characters'); return; }
    setPwSaving(true);
    try {
      await api.changePassword({ current_password: currentPw, new_password: newPw });
      setPwStatus('Password changed! Other sessions have been logged out.');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      setPwStatus(`Error: ${e.message}`);
    } finally { setPwSaving(false); }
    setTimeout(() => setPwStatus(''), 6000);
  };

  const handleSetup2fa = async () => {
    setTfaStatus('');
    setTfaLoading(true);
    try {
      const res = await api.setup2fa({ method: tfaMethod, contact: tfaContact });
      setTfaStep('verify');
      setTfaStatus(res.message);
    } catch (e) {
      setTfaStatus(`Error: ${e.message}`);
    } finally { setTfaLoading(false); }
  };

  const handleVerify2fa = async () => {
    setTfaStatus('');
    setTfaLoading(true);
    try {
      await api.verify2fa({ code: tfaCode });
      setTwofa({ enabled: true, method: tfaMethod, contact: tfaContact.slice(0, 2) + '***' });
      setTfaStep('idle');
      setTfaCode('');
      setTfaContact('');
      setTfaStatus('2FA enabled!');
    } catch (e) {
      setTfaStatus(`Error: ${e.message}`);
    } finally { setTfaLoading(false); }
    setTimeout(() => setTfaStatus(''), 4000);
  };

  const handleDisable2fa = async () => {
    setTfaLoading(true);
    try {
      await api.disable2fa();
      setTwofa({ enabled: false, method: null, contact: null });
      setTfaStep('idle');
      setTfaStatus('2FA disabled');
    } catch (e) {
      setTfaStatus(`Error: ${e.message}`);
    } finally { setTfaLoading(false); }
    setTimeout(() => setTfaStatus(''), 4000);
  };

  const pwEye = (show, setShow) => (
    <button type="button" onClick={() => setShow(!show)}
      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#adadb8', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'var(--font-pixel)', padding: '4px' }}>
      {show ? 'o.o' : '-.-'}
    </button>
  );

  return (
    <div className="dashboard">
      <h1>Settings</h1>

      {/* Profile */}
      <div className="stream-setup">
        <h2>Profile</h2>

        {/* Banner upload */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#adadb8', display: 'block', marginBottom: '8px' }}>Channel Banner</label>
          <div style={{ position: 'relative', width: '100%', height: '120px', background: '#2a2a2e', borderRadius: '4px', overflow: 'hidden', border: '1px solid #3a3a3e', cursor: 'pointer' }}
            onClick={() => document.getElementById('bannerUpload').click()}>
            {user.has_banner || user.username ? (
              <img src={`${process.env.REACT_APP_API_URL || 'http://localhost:8083'}/api/images/banner/${user.username}?t=${Date.now()}`}
                alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={e => { e.target.style.display = 'none'; }} />
            ) : null}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', opacity: 0, transition: 'opacity 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0}>
              <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700 }}>Click to upload banner</span>
            </div>
          </div>
          <input type="file" id="bannerUpload" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }}
            onChange={async e => {
              const file = e.target.files[0];
              if (!file) return;
              try {
                await api.uploadBanner(file);
                setStatus('Banner uploaded!');
                window.location.reload();
              } catch (err) { setStatus(`Error: ${err.message}`); }
              setTimeout(() => setStatus(''), 3000);
            }} />
          <div style={{ fontSize: '0.72rem', color: '#adadb8', marginTop: '4px' }}>Recommended: 1200x300, max 2MB. JPEG, PNG, GIF, or WebP.</div>
        </div>

        {/* Avatar + info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{ position: 'relative', width: 72, height: 72, borderRadius: '50%', background: '#2a2a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', overflow: 'hidden', flexShrink: 0, cursor: 'pointer', border: '2px solid #3a3a3e' }}
            onClick={() => document.getElementById('avatarUpload').click()}>
            {avatarUrl
              ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
              : (user.display_name || user.username).charAt(0).toUpperCase()}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', opacity: 0, transition: 'opacity 0.15s', borderRadius: '50%' }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0}>
              <span style={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700 }}>Edit</span>
            </div>
          </div>
          <input type="file" id="avatarUpload" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }}
            onChange={async e => {
              const file = e.target.files[0];
              if (!file) return;
              try {
                const res = await api.uploadAvatar(file);
                setUser(res.user);
                setAvatarUrl(res.url);
                setStatus('Avatar uploaded!');
              } catch (err) { setStatus(`Error: ${err.message}`); }
              setTimeout(() => setStatus(''), 3000);
            }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{displayName || user.username}</div>
            <div style={{ color: '#adadb8', fontSize: '0.85rem' }}>@{user.username}</div>
            <div style={{ fontSize: '0.72rem', color: '#666', marginTop: '2px' }}>Click avatar to upload (max 2MB)</div>
          </div>
        </div>

        <div className="form-group">
          <label>Bio</label>
          <textarea className="input" placeholder="Tell viewers about yourself..." value={bio} onChange={e => setBio(e.target.value)} maxLength={500} rows={3} style={{ resize: 'vertical', minHeight: '72px', fontFamily: 'inherit' }} />
          <div style={{ fontSize: '0.75rem', color: '#adadb8', marginTop: '4px', textAlign: 'right' }}>{bio.length}/500</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          {status && <span style={{ fontSize: '0.85rem', color: status.startsWith('Error') ? '#eb0400' : '#00c400' }}>{status}</span>}
        </div>
      </div>

      {/* Change Password */}
      <div className="stream-setup">
        <h2>Change Password</h2>
        <div className="form-group">
          <label>Current Password</label>
          <div style={{ position: 'relative' }}>
            <input className="input" type={showCurrentPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} style={{ paddingRight: '40px', marginBottom: 0 }} />
            {pwEye(showCurrentPw, setShowCurrentPw)}
          </div>
        </div>
        <div className="form-group">
          <label>New Password</label>
          <div style={{ position: 'relative' }}>
            <input className="input" type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} minLength={8} style={{ paddingRight: '40px', marginBottom: 0 }} />
            {pwEye(showNewPw, setShowNewPw)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#adadb8', marginTop: '4px' }}>Minimum 8 characters</div>
        </div>
        <div className="form-group">
          <label>Confirm New Password</label>
          <input className="input" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} style={{ marginBottom: 0 }} />
          {newPw && confirmPw && newPw !== confirmPw && (
            <div style={{ fontSize: '0.75rem', color: '#eb0400', marginTop: '4px' }}>Passwords don't match</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={handleChangePassword} disabled={pwSaving || !currentPw || !newPw || !confirmPw}>
            {pwSaving ? 'Changing...' : 'Change Password'}
          </button>
          {pwStatus && <span style={{ fontSize: '0.85rem', color: pwStatus.startsWith('Error') ? '#eb0400' : '#00c400' }}>{pwStatus}</span>}
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="stream-setup">
        <h2>Two-Factor Authentication</h2>

        {twofa.enabled ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', background: '#0e0e10', borderRadius: '6px', marginBottom: '16px' }}>
              <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.8rem', color: '#00c400' }}>ON</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>2FA is enabled</div>
                <div style={{ fontSize: '0.82rem', color: '#adadb8' }}>
                  Via {twofa.method} to {twofa.contact}
                </div>
              </div>
            </div>
            <button className="btn btn-danger" onClick={handleDisable2fa} disabled={tfaLoading}>
              {tfaLoading ? 'Disabling...' : 'Disable 2FA'}
            </button>
          </div>
        ) : tfaStep === 'idle' ? (
          <div>
            <p style={{ fontSize: '0.85rem', color: '#adadb8', marginBottom: '16px' }}>
              Add an extra layer of security. When you log in, we'll send a 6-digit code to verify it's you.
            </p>
            <div className="form-group">
              <label>Verification Method</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button className={`btn ${tfaMethod === 'email' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setTfaMethod('email')} style={{ flex: 1 }}>
                  Email
                </button>
                <button className={`btn ${tfaMethod === 'phone' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setTfaMethod('phone')} style={{ flex: 1 }}>
                  Phone
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>{tfaMethod === 'email' ? 'Email Address' : 'Phone Number'}</label>
              <input className="input"
                placeholder={tfaMethod === 'email' ? 'your@email.com' : '+1 555 123 4567'}
                value={tfaContact}
                onChange={e => setTfaContact(e.target.value)}
                style={{ marginBottom: 0 }}
              />
            </div>
            <button className="btn btn-primary" onClick={handleSetup2fa} disabled={tfaLoading || !tfaContact.trim()}>
              {tfaLoading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: '0.85rem', color: '#adadb8', marginBottom: '12px' }}>
              Enter the 6-digit code sent to your {tfaMethod}.
            </p>
            <div className="form-group">
              <label>Verification Code</label>
              <input className="input"
                placeholder="123456"
                value={tfaCode}
                onChange={e => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                style={{ marginBottom: 0, fontFamily: 'var(--font-pixel)', fontSize: '1.2rem', letterSpacing: '8px', textAlign: 'center' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-primary" onClick={handleVerify2fa} disabled={tfaLoading || tfaCode.length !== 6}>
                {tfaLoading ? 'Verifying...' : 'Verify & Enable'}
              </button>
              <button className="btn btn-secondary" onClick={() => { setTfaStep('idle'); setTfaCode(''); setTfaStatus(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {tfaStatus && <p style={{ fontSize: '0.85rem', marginTop: '12px', color: tfaStatus.startsWith('Error') ? '#eb0400' : '#00c400' }}>{tfaStatus}</p>}
      </div>

      {/* Account info */}
      <div className="stream-setup">
        <h2>Account</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Email</div>
            <div style={{ fontSize: '0.85rem', color: '#adadb8' }}>{user.email}</div>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Account Type</div>
            <div style={{ fontSize: '0.85rem', color: '#adadb8' }}>{user.is_streamer ? 'Streamer' : 'Viewer'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
